import { useCallback, useState } from "react";

import { useAppContext } from "../AppProviders";
import { UI_LIST_BULK_MAX_ITEMS } from "../api/productReportUiListBulk";
import { syncOnlyInLegacyTab } from "./compareUiListBackfill";

type CompareUiListSyncButtonProps = {
  rowTitle: string;
  onlyInLegacy: string[];
  uiList: string;
  reportType: "MISCONDUCT" | "360";
  disabled?: boolean;
};

export function CompareUiListSyncButton({
  rowTitle,
  onlyInLegacy,
  uiList,
  reportType,
  disabled = false,
}: CompareUiListSyncButtonProps) {
  const { requestApi } = useAppContext();
  const [status, setStatus] = useState<string | null>(null);
  const [running, setRunning] = useState(false);

  const onSync = useCallback(async () => {
    if (onlyInLegacy.length === 0 || disabled) return;
    setRunning(true);
    setStatus(null);
    try {
      const { updated, skipped, errors } = await syncOnlyInLegacyTab(
        requestApi,
        reportType,
        onlyInLegacy,
        uiList
      );
      setStatus(
        `Updated ${updated}, skipped ${skipped}, errors ${errors} (ui_list=${uiList})`
      );
    } catch (e) {
      setStatus(e instanceof Error ? e.message : String(e));
    } finally {
      setRunning(false);
    }
  }, [requestApi, reportType, onlyInLegacy, uiList, disabled]);

  if (onlyInLegacy.length === 0) {
    return <span className="text-gray-400 text-xs">—</span>;
  }

  const capped = onlyInLegacy.length > UI_LIST_BULK_MAX_ITEMS;

  return (
    <div className="flex flex-col gap-1">
      <button
        type="button"
        className="px-2 py-0.5 bg-indigo-700 text-white text-xs rounded disabled:opacity-50"
        disabled={disabled || running}
        title={`Set ui_list=${uiList} on reports only in legacy ${rowTitle} (max ${UI_LIST_BULK_MAX_ITEMS} per request)`}
        onClick={() => void onSync()}
      >
        {running ? "Syncing…" : `Sync ${onlyInLegacy.length} → ${uiList}`}
      </button>
      {capped ? (
        <span className="text-amber-700 text-xs">
          Sends first {UI_LIST_BULK_MAX_ITEMS} UUIDs per click; re-run to continue.
        </span>
      ) : null}
      {status ? <span className="text-xs text-gray-600">{status}</span> : null}
    </div>
  );
}
